import React, { useEffect, useRef, useMemo } from "react";
import { PlusIcon } from "@heroicons/react/24/outline";
import Select from "components/ui/Select";
import useModalStore from "store/modals";
import { useBrandTree } from "http-hooks/brand/useBrand";
import { useBrandsByVendor } from "http-hooks/agitem/useAgitem";

// Flatten the tree structure into options with proper indentation
const flattenTreeToOptions = (nodes) => {
  if (!nodes || !Array.isArray(nodes)) return [];
  
  let options = [];
  
  nodes.forEach((node) => {
    // Create indentation based on database level (1 = main brand, 2 = sub-brand)
    // Level 1 = no indent, Level 2 = indent
    const indent = node.level === 2 ? "\u00A0\u00A0\u00A0\u00A0" : "";
    
    // Add the current node as an option
    options.push({
      value: node.id,
      label: `${indent}${node.brandname}`,
      originalName: node.brandname, // Store original name without indentation
      level: node.level, // Use the actual database level
      parentid: node.parentid,
      isSubBrand: node.level === 2,
      ...node,
    });
    
    // Recursively add children (sub-brands)
    if (node.children && node.children.length > 0) {
      options = options.concat(flattenTreeToOptions(node.children));
    }
  });
  
  return options;
};

const SelectBrandAndSubbrands = ({
  value,
  onChange,
  placeholder = "Select Brand/Sub-brand",
  isDisabled = false,
  isRequired = false,
  label = "Brand/Sub-brand",
  className = "",
  filters = {},
  showLabel = true,
  vendorId = null, // Optional vendor ID to filter brands
  isMulti = false, // Add isMulti prop
  initialValue = null, // Expected: { id: "value" }
  showAddButton = true, // New prop to control plus button visibility
}) => {
  const hasInitialized = useRef(false);
  const { openModal } = useModalStore();
  
  // Use vendor-specific brands if vendorId is provided, otherwise use all brands
  const {
    isLoading: isLoadingBrandTree,
    data: { data: brandTree } = [],
  } = useBrandTree({
    noPagination: true,
    ...filters,
  }, !vendorId); // Only enabled when vendorId is not provided

  const {
    isLoading: isLoadingVendorBrands,
    data: { data: vendorBrandTree } = [],
  } = useBrandsByVendor(vendorId, !!vendorId); // Only enabled when vendorId is provided

  // Use vendor brands if available, otherwise use all brands
  const finalBrandTree = vendorId ? vendorBrandTree : brandTree;
  const isLoading = vendorId ? isLoadingVendorBrands : isLoadingBrandTree;

  // Create the flattened options
  const brandOptions = useMemo(() => {
    return flattenTreeToOptions(finalBrandTree);
  }, [finalBrandTree]);

  // Handle initial value selection
  useEffect(() => {
    if (
      !hasInitialized.current && 
      initialValue &&
      brandOptions.length > 0 && 
      !isLoading &&
      onChange
    ) {
      // Check if required field is present
      if (!initialValue.id) {
        const error = new Error(
          `SelectBrandAndSubbrands: initialValue must contain id field. Received: ${JSON.stringify(initialValue)}`
        );
        console.error(error.message);
        throw error;
      }

      const matchedOption = brandOptions.find(option => option.id === initialValue.id);
      
      if (matchedOption) {
        onChange(matchedOption);
      }
      
      hasInitialized.current = true;
    }
  }, [brandOptions, initialValue, isLoading, onChange]);

  const handleAddNew = () => {
    openModal('brand');
  };

  return (
    <div>
      {showLabel && (
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium leading-6 text-gray-900">
            {label}
          </label>
          {showAddButton && !isDisabled && (
            <button
              type="button"
              onClick={handleAddNew}
              className="ml-2 p-1 text-xs font-bold bg-black text-white rounded hover:bg-gray-800 transition-colors duration-200 flex items-center"
              title="Add new brand"
            >
              <PlusIcon className="h-3 w-3" aria-hidden="true" />
            </button>
          )}
        </div>
      )}
      <Select
        value={value}
        onChange={onChange}
        options={brandOptions}
        getOptionValue={(option) => option?.id}
        getOptionLabel={(option) => option?.label}
        placeholder={placeholder}
        isDisabled={isDisabled}
        isLoading={isLoading}
        isRequired={isRequired}
        label={label}
        className={className}
        showLabel={false}
        isClearable={true}
        isSearchable={true}
        isMulti={isMulti}
        // Custom formatting for the options
        formatOptionLabel={(option, { context }) => {
          if (context === 'menu') {
            // In dropdown menu, show with indentation
            return (
              <div style={{ 
                paddingLeft: option.level === 2 ? '16px' : '0px',
                fontWeight: option.level === 1 ? '600' : '400',
                color: option.level === 1 ? '#374151' : '#6B7280'
              }}>
                {option.originalName || option.brandname}
              </div>
            );
          }
          // In selected value, show without indentation
          return option.originalName || option.brandname;
        }}
      />
      {!showLabel && showAddButton && !isDisabled && (
        <button
          type="button"
          onClick={handleAddNew}
          className="absolute top-0 right-0 h-10 w-10 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors duration-200"
          title="Add new brand"
        >
          <PlusIcon className="h-5 w-5" aria-hidden="true" />
        </button>
      )}
    </div>
  );
};

export default SelectBrandAndSubbrands; 